// release-deploy-completion-receipt.ts
//
// Bug O (P18) + B2-2/B2-3 (design H14 bản 2, 2026-09-29).
//
// Bug O: `resumeReleaseFlowIfConfigured` runs on every `avatar autopilot run`;
// without a receipt every run after G3 approval re-executed the deploy
// recipe (measured 3 -> 4 deploy intents from one extra run).
//
// B2-2: the receipt used to be ONE file, written only after deploy AND smoke
// both passed. A deploy that succeeded followed by a failing smoke therefore
// left no trace, so the next run deployed AGAIN. Now each step leaves its own
// receipt the moment it succeeds:
//   release-state/deploy-<candidate>.json — deploy recipe exited 0
//   release-state/smoke-<candidate>.json  — smoke recipe exited 0
// The deploy receipt carries the DEPLOY hash (candidate + remote + target +
// deploy_recipe — release-manifest-hash.ts), the smoke receipt the full
// release manifest hash: fixing only the smoke recipe never re-runs a deploy
// that succeeded, and "released" means both receipts match. A process killed
// before this receipt is written is caught from the journal instead
// (release-deploy-outcome-assessment.ts, Luke 2026-09-30).
//
// B2-3: a receipt written by an older CLI (`status:"deployed_verified"`) may
// come from the removed `true` fallback — it proves nothing ran. It still
// blocks a repeat deploy, but it is reported as `legacy`, never as released.
//
// Direction of failure (Luke 2026-09-30): an ABSENT receipt reads as "not
// done"; a deploy receipt that exists but cannot be read or lacks the shape
// this module writes is `deployReceiptDamaged` — the deploy outcome is
// unknown, so the release stops for a human instead of deploying again. A
// damaged SMOKE receipt still reads as "not passed": smoke has no deploy side
// effect, re-running it is safe. Lives in the control root, outside the
// worker's worktree.
import { readFile, rm } from "node:fs/promises";
import { join } from "node:path";
import { ensureDir } from "../filesystem-helpers.js";
import { writeFileDurable } from "./durable-file-writer.js";

export type ReceiptStep = "deploy" | "smoke";

const LEGACY_STATUS = "deployed_verified";

function receiptDir(controlRoot: string): string {
  return join(controlRoot, "release-state");
}

export function receiptPath(controlRoot: string, step: ReceiptStep, candidateSha: string): string {
  return join(receiptDir(controlRoot), `${step}-${candidateSha}.json`);
}

export type ReceiptRead =
  | { state: "absent" }
  | { state: "damaged" }
  | { state: "ok"; body: Record<string, unknown> };

export async function readReceipt(
  controlRoot: string,
  step: ReceiptStep,
  candidateSha: string,
): Promise<ReceiptRead> {
  let text: string;
  try {
    text = await readFile(receiptPath(controlRoot, step, candidateSha), "utf8");
  } catch (err) {
    return (err as NodeJS.ErrnoException).code === "ENOENT"
      ? { state: "absent" }
      : { state: "damaged" };
  }
  try {
    const parsed = JSON.parse(text);
    return typeof parsed === "object" && parsed !== null && parsed.candidate_sha === candidateSha
      ? { state: "ok", body: parsed as Record<string, unknown> }
      : { state: "damaged" };
  } catch {
    return { state: "damaged" };
  }
}

/** A deploy receipt this module (or the B2-3 legacy CLI) could have written. */
export function isWellFormedDeployReceipt(body: Record<string, unknown>): boolean {
  if (body.status === LEGACY_STATUS) return true;
  return body.status === "deployed" && typeof body.deploy_hash === "string";
}

export interface ReleaseReceiptState {
  /** An older CLI's single `deployed_verified` receipt for this candidate (B2-3). */
  legacy: boolean;
  /** Deploy recipe exited 0 for this candidate + manifest. */
  deployed: boolean;
  /** Smoke recipe exited 0 for this candidate + manifest. */
  smokePassed: boolean;
  /** The deploy receipt file exists but is unreadable or malformed — outcome unknown. */
  deployReceiptDamaged: boolean;
}

export async function readReleaseReceiptState(
  controlRoot: string,
  input: { candidateSha: string; releaseManifestHash: string; releaseDeployHash: string },
): Promise<ReleaseReceiptState> {
  const deployRead = await readReceipt(controlRoot, "deploy", input.candidateSha);
  const smokeRead = await readReceipt(controlRoot, "smoke", input.candidateSha);
  const deploy =
    deployRead.state === "ok" && isWellFormedDeployReceipt(deployRead.body)
      ? deployRead.body
      : undefined;
  const smoke = smokeRead.state === "ok" ? smokeRead.body : undefined;
  return {
    legacy: deploy?.status === LEGACY_STATUS,
    deployed: deploy?.status === "deployed" && deploy.deploy_hash === input.releaseDeployHash,
    smokePassed:
      smoke?.status === "smoke_passed" && smoke.release_manifest_hash === input.releaseManifestHash,
    deployReceiptDamaged: deployRead.state !== "absent" && deploy === undefined,
  };
}

async function writeReceipt(
  controlRoot: string,
  step: ReceiptStep,
  candidateSha: string,
  body: Record<string, unknown>,
): Promise<void> {
  await ensureDir(receiptDir(controlRoot));
  const receipt = { schema_version: 2, candidate_sha: candidateSha, ...body };
  await writeFileDurable(
    receiptPath(controlRoot, step, candidateSha),
    Buffer.from(`${JSON.stringify({ ...receipt, at: new Date().toISOString() }, null, 2)}\n`),
  );
}

/**
 * Written the moment the deploy recipe exits 0 — BEFORE smoke runs (B2-2) —
 * or, with `recoveredFromOperationId`, on a later run whose journal proves
 * that operation exited 0 for this candidate + deploy step. With
 * `manualResolution` the "proof" is a person's `resolve-deploy deployed`
 * answer, and the receipt says so (review P2 M4) — it is the one deploy
 * receipt `resolve-deploy not-deployed` may take back.
 */
export async function recordDeployReceipt(
  controlRoot: string,
  input: {
    candidateSha: string;
    releaseDeployHash: string;
    g3RequestId: string;
    deployRecipeId: string;
    recoveredFromOperationId?: string;
    manualResolution?: { actor: string; at: string };
  },
): Promise<void> {
  await writeReceipt(controlRoot, "deploy", input.candidateSha, {
    status: "deployed",
    deploy_hash: input.releaseDeployHash,
    g3_request_id: input.g3RequestId,
    deploy_recipe_id: input.deployRecipeId,
    exit_code: 0,
    ...(input.recoveredFromOperationId === undefined
      ? {}
      : { recovered_from_operation_id: input.recoveredFromOperationId }),
    ...(input.manualResolution === undefined
      ? {}
      : { manual_resolution: { ...input.manualResolution, answer: "deployed" } }),
  });
}

/** Written when the smoke recipe exits 0. v1 has no rollout probe: `rollout` stays `not_observed`. */
export async function recordSmokeReceipt(
  controlRoot: string,
  input: { candidateSha: string; releaseManifestHash: string; smokeRecipeId: string },
): Promise<void> {
  await writeReceipt(controlRoot, "smoke", input.candidateSha, {
    status: "smoke_passed",
    release_manifest_hash: input.releaseManifestHash,
    smoke_recipe_id: input.smokeRecipeId,
    exit_code: 0,
    rollout: "not_observed",
  });
}

/**
 * Removes both receipts. Nothing calls this on the production path yet —
 * `rollback-execution.ts` itself has no production caller. Since 2026-09-30
 * this ALONE does not let the same candidate deploy again: a journaled
 * `succeeded` deploy for the same candidate + deploy hash rebuilds the receipt
 * (release-deploy-outcome-assessment.ts — never redeploy what the journal
 * proves ran). Whoever wires "rolled back, redeploy the same commit" must also
 * journal that decision and teach the assessment to honour it. Absent
 * receipts are a no-op.
 */
export async function clearDeployCompletionReceipt(
  controlRoot: string,
  candidateSha: string,
): Promise<void> {
  await rm(receiptPath(controlRoot, "deploy", candidateSha), { force: true });
  await rm(receiptPath(controlRoot, "smoke", candidateSha), { force: true });
}
